import React, { useState } from 'react';
import { X, CreditCard, Building, Phone, Users, Calendar, CheckCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';

const initialFormState = {
  // Basic information
  fullName: '',
  email: '',
  companyName: '',
  phone: '',

  // Business details
  currentDailyCalls: '',
  currentCallHandler: 'owner',
  desiredStartDate: '',

  // Selected plan
  selectedPlan: null,

  // Payment
  acceptPaymentTerms: false,

  // TCPA consent (required to call/text the number provided)
  callConsent: false,
  textConsent: false
};

const SubscriptionInterestForm = ({ isOpen, onClose, preselectedPlan = null }) => {
  const [formData, setFormData] = useState({
    ...initialFormState,
    selectedPlan: preselectedPlan
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionComplete, setSubmissionComplete] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => {
        const updated = { ...prev };
        delete updated[name];
        return updated;
      });
    }
  };

  const validate = () => {
    const nextErrors = {};

    if (!formData.fullName.trim()) {
      nextErrors.fullName = 'Full name is required';
    }

    if (!formData.email.trim()) {
      nextErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      nextErrors.email = 'Please enter a valid email address';
    }

    if (!formData.companyName.trim()) {
      nextErrors.companyName = 'Company name is required';
    }

    if (!formData.phone.trim()) {
      nextErrors.phone = 'Phone number is required';
    }

    if (!formData.currentDailyCalls) {
      nextErrors.currentDailyCalls = 'Please estimate your daily call volume';
    }

    if (!formData.desiredStartDate) {
      nextErrors.desiredStartDate = 'Please select when you want to get started';
    }

    if (!formData.selectedPlan) {
      nextErrors.selectedPlan = 'Please select a subscription plan';
    }

    if (!formData.acceptPaymentTerms) {
      nextErrors.acceptPaymentTerms = 'You must accept the payment terms';
    }

    if (!formData.callConsent) {
      nextErrors.callConsent = 'Please consent to be contacted by phone';
    }

    return nextErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      toast.error('Please complete all required fields');
      return;
    }

    setIsSubmitting(true);

    try {
      // Submit the subscription interest to the backend
      const response = await fetch(`${import.meta.env.VITE_API_URL || ''}/api/subscription-interest`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ...formData, consentAt: new Date().toISOString() })
      });

      if (!response.ok) {
        throw new Error('Failed to submit subscription interest');
      }

      const data = await response.json();

      setSubmissionComplete(true);
      toast.success('Thank you! Our team will contact you within 24 hours to complete your onboarding.');

      // Reset form after a delay
      setTimeout(() => {
        handleClose();
      }, 3000);

    } catch (error) {
      console.error('Subscription interest submission error:', error);
      toast.error('Failed to submit. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({ ...initialFormState, selectedPlan: preselectedPlan });
    setErrors({});
    setSubmissionComplete(false);
    setIsSubmitting(false);
    onClose();
  };

  if (!isOpen) return null;

  if (submissionComplete) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h2>
          <p className="text-gray-600 mb-6">
            We've received your subscription request. A member of our team will reach out within 24 hours to complete your onboarding and answer any questions.
          </p>
          <button
            onClick={handleClose}
            className="bg-indigo-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-indigo-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full my-8">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-emerald-500 text-white p-6 rounded-t-2xl">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-2xl font-bold mb-2">Start Your Subscription</h2>
              <p className="text-indigo-100">
                Tell us about your business and we'll get you set up with Alfred Voice
              </p>
            </div>
            <button
              onClick={handleClose}
              className="text-white hover:bg-white/20 rounded-lg p-1 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Contact Information */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Users className="w-5 h-5 mr-2 text-indigo-600" />
              Contact Information
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="fullName"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 rounded-lg border ${errors.fullName ? 'border-red-300' : 'border-gray-300'} focus:ring-2 focus:ring-indigo-500 focus:border-transparent`}
                  placeholder="John Smith"
                />
                {errors.fullName && <p className="mt-1 text-sm text-red-500">{errors.fullName}</p>}
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 rounded-lg border ${errors.email ? 'border-red-300' : 'border-gray-300'} focus:ring-2 focus:ring-indigo-500 focus:border-transparent`}
                  placeholder="john@company.com"
                />
                {errors.email && <p className="mt-1 text-sm text-red-500">{errors.email}</p>}
              </div>

              <div>
                <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-1">
                  Company Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="companyName"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 rounded-lg border ${errors.companyName ? 'border-red-300' : 'border-gray-300'} focus:ring-2 focus:ring-indigo-500 focus:border-transparent`}
                  placeholder="ABC Company Inc."
                />
                {errors.companyName && <p className="mt-1 text-sm text-red-500">{errors.companyName}</p>}
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 rounded-lg border ${errors.phone ? 'border-red-300' : 'border-gray-300'} focus:ring-2 focus:ring-indigo-500 focus:border-transparent`}
                  placeholder="+1 (555) 123-4567"
                />
                {errors.phone && <p className="mt-1 text-sm text-red-500">{errors.phone}</p>}
              </div>
            </div>
          </div>

          {/* Business Details */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Building className="w-5 h-5 mr-2 text-indigo-600" />
              Business Details
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="currentDailyCalls" className="block text-sm font-medium text-gray-700 mb-1">
                  Current Number of Daily Calls <span className="text-red-500">*</span>
                </label>
                <select
                  id="currentDailyCalls"
                  name="currentDailyCalls"
                  value={formData.currentDailyCalls}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 rounded-lg border ${errors.currentDailyCalls ? 'border-red-300' : 'border-gray-300'} focus:ring-2 focus:ring-indigo-500 focus:border-transparent`}
                >
                  <option value="">Select range...</option>
                  <option value="1-10">1-10 calls/day</option>
                  <option value="11-25">11-25 calls/day</option>
                  <option value="26-50">26-50 calls/day</option>
                  <option value="51-100">51-100 calls/day</option>
                  <option value="100+">100+ calls/day</option>
                </select>
                {errors.currentDailyCalls && <p className="mt-1 text-sm text-red-500">{errors.currentDailyCalls}</p>}
              </div>

              <div>
                <label htmlFor="currentCallHandler" className="block text-sm font-medium text-gray-700 mb-1">
                  Who Currently Handles Calls? <span className="text-red-500">*</span>
                </label>
                <select
                  id="currentCallHandler"
                  name="currentCallHandler"
                  value={formData.currentCallHandler}
                  onChange={handleChange}
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="owner">Business owner</option>
                  <option value="receptionist">Receptionist/Front desk</option>
                  <option value="team">Team members</option>
                  <option value="answering-service">Answering service</option>
                  <option value="voicemail">Goes to voicemail</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label htmlFor="desiredStartDate" className="block text-sm font-medium text-gray-700 mb-1">
                  When Do You Want to Get Started? <span className="text-red-500">*</span>
                </label>
                <select
                  id="desiredStartDate"
                  name="desiredStartDate"
                  value={formData.desiredStartDate}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 rounded-lg border ${errors.desiredStartDate ? 'border-red-300' : 'border-gray-300'} focus:ring-2 focus:ring-indigo-500 focus:border-transparent`}
                >
                  <option value="">Select timeframe...</option>
                  <option value="immediately">Immediately (within 1 week)</option>
                  <option value="this-month">This month</option>
                  <option value="next-month">Next month</option>
                  <option value="exploring">Just exploring options</option>
                </select>
                {errors.desiredStartDate && <p className="mt-1 text-sm text-red-500">{errors.desiredStartDate}</p>}
              </div>

              <div>
                <label htmlFor="selectedPlan" className="block text-sm font-medium text-gray-700 mb-1">
                  Selected Plan <span className="text-red-500">*</span>
                </label>
                <select
                  id="selectedPlan"
                  name="selectedPlan"
                  value={formData.selectedPlan || ''}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 rounded-lg border ${errors.selectedPlan ? 'border-red-300' : 'border-gray-300'} focus:ring-2 focus:ring-indigo-500 focus:border-transparent`}
                >
                  <option value="">Select a plan...</option>
                  <option value="core">Core Package - $149/month</option>
                  <option value="premium">Premium Package - $449/month</option>
                  <option value="enterprise">Enterprise Package - $899/month</option>
                </select>
                {errors.selectedPlan && <p className="mt-1 text-sm text-red-500">{errors.selectedPlan}</p>}
              </div>
            </div>
          </div>

          {/* Payment Terms Notice */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start">
              <CreditCard className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h4 className="font-semibold text-gray-900 mb-2">Payment Information</h4>
                <p className="text-sm text-gray-700 mb-3">
                  A member of our team will contact you to:
                </p>
                <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                  <li>Complete a personalized onboarding session</li>
                  <li>Configure Alfred Voice for your specific business needs</li>
                  <li>Securely collect payment information</li>
                  <li>Answer any questions you may have</li>
                </ul>
                <p className="text-sm text-gray-700 mt-3 font-medium">
                  Your card will NOT be charged until after your onboarding session is complete and you're ready to go live.
                </p>
              </div>
            </div>
          </div>

          {/* Terms Acceptance */}
          <div>
            <label className="flex items-start">
              <input
                type="checkbox"
                name="acceptPaymentTerms"
                checked={formData.acceptPaymentTerms}
                onChange={handleChange}
                className="mt-1 mr-2 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <span className="text-sm text-gray-700">
                I understand that Relay Work will contact me to complete onboarding and securely collect payment information. No charges will be made until I authorize them after the onboarding session. <span className="text-red-500">*</span>
              </span>
            </label>
            {errors.acceptPaymentTerms && <p className="mt-1 text-sm text-red-500">{errors.acceptPaymentTerms}</p>}
          </div>

          {/* TCPA Consent */}
          <div>
            <label className="flex items-start">
              <input
                type="checkbox"
                name="callConsent"
                checked={formData.callConsent}
                onChange={handleChange}
                className="mt-1 mr-2 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <span className="text-sm text-gray-700">
                I agree to be contacted at the phone number provided, including by automated technology or AI voice calls, about Alfred Voice. Consent is not a condition of purchase. <span className="text-red-500">*</span>
              </span>
            </label>
            {errors.callConsent && <p className="mt-1 text-sm text-red-500">{errors.callConsent}</p>}
            <label className="flex items-start mt-3">
              <input
                type="checkbox"
                name="textConsent"
                checked={formData.textConsent}
                onChange={handleChange}
                className="mt-1 mr-2 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <span className="text-sm text-gray-700">
                OK to text me at this number (msg/data rates may apply; reply STOP to opt out).
              </span>
            </label>
          </div>

          {/* Submit Button */}
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-indigo-600 to-emerald-500 text-white rounded-xl font-semibold hover:from-indigo-700 hover:to-emerald-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Request'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SubscriptionInterestForm;
